import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import * as moment from 'moment';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import {
  API_BASE_URL,
  AttachmentVo,
  ClinicalClient,
  ClinicalNewClient,
  ClinicalPatientClient,
  MarkPathologyReportAsRead,
  PathologyReportWorkflowVo,
  PatientDashboardHistoryEventDto,
  PatientVo,
  ProviderCommunicationEmailVo,
  RestApiResultOfBoolean,
  Status2,
  Status3,
} from './api-client.service';
import { MerakiClientService, PathReportFollowuActions } from './meraki-client.service';
import { PathologyReportFirestoreVo } from './meraki-models/meraki-models';
import { MerakiLookupService } from './meraki/meraki-lookup.service';
import { PathologyLabLookup } from './models/lab-mnemonic-mapping.model';
import { TimelineEventType } from './patients.service';
import { ProviderService } from './provider.service';
import { PathCentreFilter } from './state/provider/provider.store';

@Injectable({
  providedIn: 'root',
})
export class PathologyService {
  private baseUrl: string;

  constructor(
    private http: HttpClient,
    private clinicalClient: ClinicalClient,
    private clinicalNewClient: ClinicalNewClient,
    private clinicalPatientClient: ClinicalPatientClient,
    private merakiClientService: MerakiClientService,
    private merakiLookupService: MerakiLookupService,
    private providerService: ProviderService,
    @Inject(API_BASE_URL) baseUrl: string
  ) {
    this.baseUrl = baseUrl;
  }

  getReportId(practiceId: string, workflowId: string) {
    return this.clinicalClient.getPathologyReportWorkflow(practiceId, workflowId);
  }

  getPathReport(practiceTenantId: string, pathReportId: string) {
    return this.merakiClientService.getPathologyReport(practiceTenantId, pathReportId);
  }

  getPathReportWithHtml(practiceId: string, practiceTenantId: string, billingNo: string, pathReportId: string) {
    return this.merakiClientService.getPathologyReport(practiceTenantId, pathReportId).pipe(
      switchMap(report => this.getPathReportXml(billingNo, pathReportId).pipe(map(xml => ({ report, xml })))),
      switchMap(({ report, xml }) => this.xmlToHtml(practiceId, xml).pipe(map(html => ({ ...report, OriginalMessage: html }))))
    );
  }

  getPathReportXml(billingNo: string, reportId: string) {
    return this.merakiClientService.getPathologyReportXml(billingNo, reportId);
  }

  getPathReportPdf(billingNo: string, reportId: string) {
    return this.merakiClientService.getPathologyReportPdf(billingNo, reportId);
  }

  xmlToHtml(practiceId: string, xml: string) {
    return this.clinicalNewClient.pathXmlToHtml(practiceId, xml).pipe(map(html => html.Data));
  }

  getPathReportHtml(practiceId: string, reportId: string): Observable<string> {
    return this.http.get(`${this.baseUrl}/api/v1/${practiceId}/clinical/pathologyreports/${reportId}/html`, { responseType: 'text' });
  }

  getPathReportPatient(doctorId: string, reportId: string): Observable<PatientVo> {
    return this.clinicalClient.getPathologyReportPatient(doctorId, reportId);
  }

  getReportsByStatus(practiceId: string, status: Status2) {
    return this.clinicalClient.getPathologyReportWorkflowsAll(practiceId, status);
  }

  getUndeliveredReports(practiceId: string) {
    return this.clinicalClient.getUndeliveredPathologyReports(practiceId);
  }

  getUndeliveredReportsByDateRange(practiceId: string, pageNumber: number = 1, pageSize: number = 10) {
    const fromDate = moment().subtract(1, 'months').format('YYYY-MM-DD');
    const toDate = moment().add(1, 'd').format('YYYY-MM-DD');

    return this.clinicalClient.getUndeliveredPathologyReportsByDateRange(practiceId, fromDate, toDate, pageSize, pageNumber);
  }

  redeliveryReport(tenantId: string, pathologyReportId: string, patientIds: string[] = []) {
    return this.clinicalClient.redeliverPathResult(tenantId, pathologyReportId, { MatchedPatientIds: patientIds });
  }

  ignoreReport(tenantId: string, pathologyReportId: string) {
    return this.clinicalClient.ignorePathResult(tenantId, pathologyReportId);
  }

  getReportCount(practiceId: string, status: Status3) {
    return this.clinicalClient.getPathologyReportWorkflowsCount(practiceId, status);
  }

  /**
   * Local calendar day (start → end of day), same window used by the sidebar pathology badge
   * ({@link getReportsUnactionedForToday}) and HBC Go home "Today's Pathology Results".
   */
  private buildTodayPathCentreFilter(opts: { unreadOnly: boolean }): PathCentreFilter {
    return {
      DateRange: null,
      FromDate: moment().startOf('day'),
      ToDate: moment().endOf('day'),
      ReadState: false,
      UnreadState: opts.unreadOnly,
      InTaskListState: false,
      SmsSentState: false,
      EmailSentState: false,
      MyPatientsState: false,
    };
  }

  /** Unread delivered reports received today (sidebar / todayReports$). */
  getReportsUnactionedForToday(practiceId: string) {
    // todo can be improved by displaying only unactioned reports for doctor ('my patients')
    return this.filterPathologyResults(practiceId, null, this.buildTodayPathCentreFilter({ unreadOnly: true }));
  }

  /** All delivered reports received today (read + unread) — HBC Go home panel, Pathology Centre parity. */
  getDeliveredPathologyReportsForToday(practiceTenantId: string): Observable<PathologyReportFirestoreVo[]> {
    return this.filterPathologyResults(practiceTenantId, null, this.buildTodayPathCentreFilter({ unreadOnly: false }));
  }

  /** All delivered reports (read + unread) over a rolling N-day window — HBC Go home panel range toggle. */
  getDeliveredPathologyReportsForLastNDays(practiceTenantId: string, days: number): Observable<PathologyReportFirestoreVo[]> {
    return this.filterPathologyResults(practiceTenantId, null, this.buildLastNDaysPathCentreFilter(days, { unreadOnly: false }));
  }

  /** Rolling N-day window ending end-of-today — HBC Go home "Last N days" toggle. */
  private buildLastNDaysPathCentreFilter(days: number, opts: { unreadOnly: boolean }): PathCentreFilter {
    return {
      DateRange: null,
      FromDate: moment()
        .subtract(days - 1, 'd')
        .startOf('day'),
      ToDate: moment().endOf('day'),
      ReadState: false,
      UnreadState: opts.unreadOnly,
      InTaskListState: false,
      SmsSentState: false,
      EmailSentState: false,
      MyPatientsState: false,
    };
  }

  getReportsForCurrentWeekByStatus(tenantId: string, status: string) {
    const fromDate = moment().subtract(7, 'd').format('YYYY-MM-DD');
    const toDate = moment().add(1, 'd').format('YYYY-MM-DD');

    return this.getPathologyReportWorkflowsBasedOnDatesAndStatus(tenantId, status, fromDate, toDate);
  }

  filterUndeliveredPathologyResults(practiceTenantId: string, filter: any): Observable<PathologyReportFirestoreVo[]> {
    let dateFrom = null;
    let dateTo = null;
    if (filter.DateRange === 'LastWeek') {
      dateFrom = moment().subtract(7, 'd').toDate();
      dateTo = moment().add(1, 'd').toDate();
    } else if (filter.DateRange === 'LastMonth') {
      dateFrom = moment().subtract(1, 'months').toDate();
      dateTo = moment().add(1, 'd').toDate();
    } else if (filter.DateRange === 'Custom') {
      dateFrom = filter.FromDate?.startOf('day').toDate() || null;
      dateTo = filter.ToDate?.endOf('day').toDate() || null;
    }
    if (!dateFrom && !dateTo) {
      return of();
    }
    return this.merakiClientService.getUndeliveredPathologyReports(practiceTenantId, dateFrom, dateTo);
  }

  searchPathologyResults(practiceTenantId: string, patientIds: string[]): Observable<PathologyReportFirestoreVo[]> {
    return this.merakiClientService.getPathologyReportsByPatientIds(practiceTenantId, patientIds);
  }

  getAllPathologyReportsForPatient(practiceTenantId: string, patientIds: string): Observable<PathologyReportFirestoreVo[]> {
    return this.merakiClientService.getAllPathologyReportsForPatient(practiceTenantId, patientIds);
  }

  filterPathologyResults(
    practiceTenantId: string,
    primaryProviderId: string,
    filter: PathCentreFilter
  ): Observable<PathologyReportFirestoreVo[]> {
    let dateFrom = filter.FromDate?.startOf('day').toDate() || null;
    let dateTo = filter.ToDate?.endOf('day').toDate() || null;
    if (filter.DateRange === 'LastWeek') {
      dateFrom = moment().subtract(7, 'd').toDate();
      dateTo = moment().add(1, 'd').toDate();
    } else if (filter.DateRange === 'LastMonth') {
      dateFrom = moment().subtract(1, 'months').toDate();
      dateTo = moment().add(1, 'd').toDate();
    }

    if (!dateFrom && !dateTo) {
      return of();
    }

    const reportStates = [filter.ReadState && 'Read', filter.UnreadState && 'Unread'].filter(s => s);
    const followupActions = [
      filter.InTaskListState && PathReportFollowuActions.InTaskList,
      filter.SmsSentState && PathReportFollowuActions.SmsSent,
      filter.EmailSentState && PathReportFollowuActions.EmailSent,
    ].filter(s => s);

    // atm local search implemented based on assumption that we still need to use date range from the tab,
    // so as result it become possible to load all path reports for that date range and just do filtering in memory

    return this.merakiClientService
      .getPathologyReports(practiceTenantId, primaryProviderId, dateFrom, dateTo, reportStates, followupActions)
      .pipe(map(result => result.filter(s => (!!primaryProviderId ? s.PrimaryProviderId === primaryProviderId : true))));
  }

  linkPathReportToPatient(practiceTenantId, primaryProviderId, pathologyReportId, patientIds: string[]) {
    return this.merakiClientService.linkPathReportToPatient(practiceTenantId, primaryProviderId, pathologyReportId, patientIds).pipe(
      // add path item on timeline(s) as we linked report
      first(),
      switchMap(() => this.merakiClientService.getPathologyReportWithXml(practiceTenantId, pathologyReportId).pipe(first())),
      map(report => [
        ...patientIds.map(p => ({
          patientId: p,
          entries: this.buildTimelineEntriesForPath(report, pathologyReportId, p),
        })),
      ]),
      catchError(err => {
        console.error('error on insert', err);
        return of(null);
      }),
      switchMap(items =>
        forkJoin([...items.map(item => this.clinicalNewClient.addPatientTimelineEntries(primaryProviderId, item.patientId, item.entries))])
      )
    );
  }

  buildTimelineEntriesForPath(
    report: PathologyReportFirestoreVo,
    pathologyReportId: string,
    patientId: string
  ): PatientDashboardHistoryEventDto[] {
    return [
      ...report.PathologyReport.Patient.Requisition?.map(requisition => ({
        PathologyReportId: pathologyReportId,
        PatientId: patientId,
        ParentReferenceId: pathologyReportId,
        Type: TimelineEventType.PathologyTestType,
        Code: requisition.orderNo,
        ParentDescription: report.PathologyReport.Sender.name,
        Description: requisition.Orders?.map(i => i.code).join(',') || '',
        Narrative: 'Ordered: ' + requisition.Orders?.map(i => i.abbreviatedName || i.name).join(',') || '',
        DateTime: report.ReceivedDate,
      })),
    ];
  }

  getReportsForCurrentMonthByStatus(tenantId: string, status: string, pageNumber: number = 1, pageSize: number = 10) {
    const fromDate = moment().subtract(1, 'months').format('YYYY-MM-DD');
    const toDate = moment().add(1, 'd').format('YYYY-MM-DD');

    const statusDic: { [key: string]: any } = {
      Open: 0,
      Read: 1,
      Urgent: 2,
      Hidden: 3,
    };

    return this.clinicalClient.getPathologyReportWorkflowsBasedOnDatesAndStatusPaged(
      tenantId,
      fromDate,
      toDate,
      statusDic[status],
      pageSize,
      pageNumber
    );
  }

  getReportsLinkedToTenant(practiceId: string) {
    return this.clinicalClient.getPathologyReportWorkflowsForProvider(practiceId);
  }

  getReportsLinkedToTenantByDateRange(practiceId: string, pageNumber: number = 1, pageSize: number = 10) {
    const fromDate = moment().subtract(1, 'months').toDate();
    const toDate = moment().add(1, 'd').toDate();

    return this.clinicalClient.getPathologyReportWorkflowsForProvider(practiceId).pipe(
      map(data => data.filter(f => new Date(f.ReceivedDate) >= fromDate && new Date(f.ReceivedDate) <= toDate)),
      map(data => data.slice(0, pageSize))
    );
  }

  getReportsBySearch(practiceId: string, query: string) {
    return this.clinicalClient.getPathologyReport(practiceId, query);
  }

  download(html: string, password?: string) {
    return this.clinicalClient.saveHtmlToPdf(html, null, password);
  }

  downloadReport(practiceId: string, accessToken: string, html: string, password?: string) {
    return this.clinicalClient.saveHtmlToPdf(html, null, password).pipe(
      map(data => {
        const url = `${this.baseUrl}/api/v1/document?url=${data.Data}&access_token=${accessToken}&practiceId=${practiceId}`;
        return { fullUrl: url, documentUrl: data.Data };
      })
    );
  }

  getLinkedPatients(practiceId: string, reportId: string) {
    return this.clinicalClient.getLinkedPatientDashboards(practiceId, reportId);
  }

  markPathologyReportAsRead(comand: MarkPathologyReportAsRead) {
    return this.clinicalClient.markPathologyReportAsRead(comand.PracticeId, comand.PathologyReportWorkflowId, comand);
  }

  updatePathologyStatus(practiceTenantId, pathologyReportId, status: 'Read' | 'Unread') {
    return this.merakiClientService.updatePathologyReportStatus(practiceTenantId, pathologyReportId, status);
  }

  updatePathReportStatuses(group: PathologyReportFirestoreVo[], status: 'Read' | 'Unread') {
    return this.merakiClientService.updatePathologyReportStatuses(group, status);
  }

  updatePathologyDeliveryStatus(practiceTenantId, pathologyReportId, status: 'Undelivered' | 'Delivered' | 'Ignored') {
    return this.merakiClientService.updatePathologyDeliveryStatus(practiceTenantId, pathologyReportId, status);
  }

  addFollowuActionForPathReport(practiceTenantId, pathologyReport, action: { Type: string; Date: Date }) {
    return this.merakiClientService.addFollowuActionForPathReport(practiceTenantId, pathologyReport, action);
  }

  markPathologyReportsAsRead(practiceid: string, workflowIds: string[]): Observable<RestApiResultOfBoolean> {
    return this.clinicalClient.markPathologyReportsAsRead(practiceid, workflowIds);
  }

  emailReport(report: PathologyReportWorkflowVo, email: ProviderCommunicationEmailVo) {
    const newAttachment: AttachmentVo = {
      Title: 'Pathology results',
      Type: 'Pathology results',
      HtmlBody: report.OriginalMessage,
    };

    email.Attachments = [newAttachment];

    return this.clinicalPatientClient.sendCommunication(report.PracticeId, report.PatientIds[0], email);
  }

  encryptPdf(pdfBytes: string, password: string) {
    return this.clinicalClient.encryptPdf(pdfBytes, password);
  }

  saveEncryptPdf(pdfBytes: string, password: string) {
    return this.clinicalClient.saveEncryptPdf(pdfBytes, password);
  }

  private getStatus(status: string): Status2 {
    return status === 'Open'
      ? Status2._0 // UNREAD
      : status === 'Undelivered'
        ? Status2._1 // undelivered
        : status === 'Urgent'
          ? Status2._2 // FOLLOW-UP
          : Status2._0;
  }

  private getPathologyReportWorkflowsBasedOnDatesAndStatus(
    practiceId: string,
    status: string,
    fromDate: string,
    toDate: string
  ): Observable<PathologyReportWorkflowVo[]> {
    const url =
      this.baseUrl + `/api/v1/${practiceId}/clinical/pathologyreportworkflows/fromdate/${fromDate}/todate/${toDate}/status/${status}`;

    return this.http.get(url).pipe(map(response => response as PathologyReportWorkflowVo[]));
  }

  /**
   * Gets list of all supported pathology labs from Configuration/PathologyLabs lookup
   * @returns Observable of pathology labs with names, sender codes, and icons
   */
  getAllPathologyLabs(): Observable<PathologyLabLookup[]> {
    return this.merakiLookupService.getAllPathologyLabs().pipe(
      catchError(err => {
        console.error('Failed to load pathology labs:', err);
        return of([]);
      })
    );
  }

  /**
   * Extracts configured mnemonic codes from provider options
   * @returns Observable of map from sender code to doctor mnemonic code
   */
  getConfiguredMnemonicCodesMap(): Observable<{ [senderCode: string]: string }> {
    return this.providerService.providerOptions$.pipe(
      map(options => {
        const mnemonicCodesMap: { [senderCode: string]: string } = {};

        if (!options || Object.keys(options).length === 0) {
          return mnemonicCodesMap;
        }

        Object.entries(options).forEach(([key, value]) => {
          if (key.startsWith('PathologyMnemonicCodes:') && value) {
            const senderCode = key.replace('PathologyMnemonicCodes:', '');
            mnemonicCodesMap[senderCode] = value as string;
          }
        });

        return mnemonicCodesMap;
      })
    );
  }

  /**
   * Gets the count of unique labs that have been configured with mnemonic codes
   * @returns Observable of the number of configured labs
   */
  getConfiguredLabsCount(): Observable<number> {
    return combineLatest([this.getAllPathologyLabs(), this.getConfiguredMnemonicCodesMap()]).pipe(
      map(([labs, mnemonicCodesMap]) => {
        if (Object.keys(mnemonicCodesMap).length === 0 || !labs?.length) {
          return 0;
        }

        const configuredSenderCodes = new Set(Object.keys(mnemonicCodesMap));
        const configuredLabIds = new Set<string>();

        labs.forEach(lab => {
          if (lab.senderCodes.some(code => configuredSenderCodes.has(code))) {
            configuredLabIds.add(lab.labId);
          }
        });

        return configuredLabIds.size;
      })
    );
  }

  /**
   * Gets the appropriate icon path for a lab based on its name
   * Returns asset path to lab-specific logo or generic pathology icon
   * @param labName The name of the lab (case-insensitive)
   * @returns Path to the lab icon image
   */
  getLabIcon(labName: string): string {
    const labNameLower = labName?.toLocaleLowerCase() || '';

    // Legacy lab logos (moved to lab-logos folder)
    if (labNameLower.includes('ampath')) {
      return '/assets/images/lab-logos/path_ampath.png';
    }
    if (labNameLower.includes('lancet')) {
      return '/assets/images/lab-logos/path_lancet.png';
    }
    if (labNameLower.includes('dietrich') || labNameLower.includes('pathcare')) {
      return '/assets/images/lab-logos/path_pathcare.png';
    }
    if (labNameLower.includes('vermaak')) {
      return '/assets/images/lab-logos/path_vermaak.png';
    }

    // New lab logos (downloaded from websites)
    if (labNameLower.includes('jdj')) {
      return '/assets/images/lab-logos/jdj-logo.png';
    }
    if (labNameLower.includes('flowpath')) {
      return '/assets/images/lab-logos/flowpath-logo.png';
    }
    if (labNameLower.includes('neuberg')) {
      return '/assets/images/lab-logos/neuberg-logo.png';
    }
    if (labNameLower.includes('mediclinic')) {
      return '/assets/images/lab-logos/mediclinic-logo.png';
    }
    if (labNameLower.includes('hamadi')) {
      return '/assets/images/lab-logos/hamadilab-logo.png';
    }
    if (labNameLower.includes('inception')) {
      return '/assets/images/lab-logos/inception-logo.png';
    }
    if (labNameLower.includes('universal')) {
      return '/assets/images/lab-logos/universal-logo.png';
    }
    if (labNameLower.includes('zarpath')) {
      return '/assets/images/lab-logos/zarpath-logo.png';
    }
    if (labNameLower.includes('toga')) {
      return '/assets/images/lab-logos/toga-logo.png';
    }
    if (labNameLower.includes('pathp') || labNameLower.includes('path p')) {
      return '/assets/images/lab-logos/pathp-logo.png';
    }
    if (labNameLower.includes('path 24') || labNameLower.includes('path24')) {
      return '/assets/images/lab-logos/path24-logo.png';
    }
    if (labNameLower.includes('pathlife')) {
      return '/assets/images/lab-logos/pathlife-logo.png';
    }
    if (labNameLower.includes('sa path') || labNameLower.includes('sapath')) {
      return '/assets/images/lab-logos/sapath-logo.png';
    }

    // Default fallback for unknown labs
    return '/assets/icons/pathology_unknown.svg';
  }
}
